'use client'

import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { MdArrowBack, MdSave, MdMedication } from 'react-icons/md'
import {
    getMedication,
    createMedication,
    updateMedication,
    CreateMedicationData,
    MEDICATION_TYPE_LABELS,
    MEDICATION_CONTROL_TYPE_LABELS,
    STORAGE_TEMPERATURE_LABELS,
    MEDICATION_UNIT_LABELS,
    MedicationType,
    MedicationControlType,
    StorageTemperature,
    MedicationUnit,
} from '@/lib/api/stock/medication-api'

export default function MedicationFormPage() {
    const navigate = useNavigate()
    const { id } = useParams()
    const isEditing = !!id

    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [formData, setFormData] = useState<CreateMedicationData>({
        name: '',
        genericName: '',
        manufacturer: '',
        anvisaRegistry: '',
        barcode: '',
        type: 'MED_GENERIC',
        controlType: 'CONTROL_NONE',
        requiresSngpc: false,
        storageTemperature: 'TEMP_AMBIENT',
        unit: 'UNIT_PILL',
        concentration: '',
        presentation: '',
        therapeuticClass: '',
        minimumStock: 10,
        maximumStock: 100,
        reorderPoint: 20,
        isHighAlert: false,
        storageInstructions: '',
        specialPrecautions: '',
    })

    useEffect(() => {
        if (isEditing) {
            loadMedication()
        }
    }, [id])

    const loadMedication = async () => {
        setLoading(true)
        try {
            const response = await getMedication(Number(id))
            if (response.success) {
                const med = response.result
                setFormData({
                    name: med.name,
                    genericName: med.genericName || '',
                    manufacturer: med.manufacturer || '',
                    anvisaRegistry: med.anvisaRegistry || '',
                    barcode: med.barcode || '',
                    type: med.type,
                    controlType: med.controlType,
                    requiresSngpc: med.requiresSngpc,
                    storageTemperature: med.storageTemperature,
                    unit: med.unit,
                    concentration: med.concentration || '',
                    presentation: med.presentation || '',
                    therapeuticClass: med.therapeuticClass || '',
                    minimumStock: med.minimumStock,
                    maximumStock: med.maximumStock,
                    reorderPoint: med.reorderPoint,
                    isHighAlert: med.isHighAlert,
                    storageInstructions: med.storageInstructions || '',
                    specialPrecautions: med.specialPrecautions || '',
                })
            } else {
                toast.error(response.error || 'Erro ao carregar medicamento')
                navigate('/stock/medications')
            }
        } catch (error) {
            toast.error('Erro ao carregar medicamento')
            navigate('/stock/medications')
        } finally {
            setLoading(false)
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!formData.name.trim()) {
            toast.error('Nome é obrigatório')
            return
        }

        setSaving(true)
        try {
            const response = isEditing
                ? await updateMedication(Number(id), formData)
                : await createMedication(formData)

            if (response.success) {
                toast.success(
                    isEditing
                        ? 'Medicamento atualizado com sucesso'
                        : 'Medicamento criado com sucesso'
                )
                navigate('/stock/medications')
            } else {
                toast.error(response.error || 'Erro ao salvar medicamento')
            }
        } catch (error) {
            toast.error('Erro ao salvar medicamento')
        } finally {
            setSaving(false)
        }
    }

    const handleControlTypeChange = (controlType: MedicationControlType) => {
        const requiresSngpc = controlType !== 'CONTROL_NONE'
        setFormData({ ...formData, controlType, requiresSngpc })
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">Carregando...</p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center gap-4">
                <button
                    onClick={() => navigate('/stock/medications')}
                    className="btn btn-ghost btn-sm"
                >
                    <MdArrowBack className="text-xl" />
                </button>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                        <MdMedication className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            {isEditing
                                ? 'Editar Medicamento'
                                : 'Novo Medicamento'}
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {isEditing
                                ? 'Atualize os dados do medicamento'
                                : 'Preencha os dados do medicamento'}
                        </p>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                    <h2 className="text-lg font-semibold mb-4">
                        Informações Básicas
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="form-control w-full md:col-span-2">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Nome Comercial{' '}
                                    <span className="text-error">*</span>
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full focus:input-primary"
                                value={formData.name}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        name: e.target.value,
                                    })
                                }
                                required
                            />
                        </div>
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Nome Genérico
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full focus:input-primary"
                                value={formData.genericName}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        genericName: e.target.value,
                                    })
                                }
                            />
                        </div>
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Fabricante
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full focus:input-primary"
                                value={formData.manufacturer}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        manufacturer: e.target.value,
                                    })
                                }
                            />
                        </div>
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Registro ANVISA
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full focus:input-primary"
                                value={formData.anvisaRegistry}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        anvisaRegistry: e.target.value,
                                    })
                                }
                            />
                        </div>
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Código de Barras
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full focus:input-primary"
                                value={formData.barcode}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        barcode: e.target.value,
                                    })
                                }
                            />
                        </div>
                    </div>
                </div>

                <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                    <h2 className="text-lg font-semibold mb-4">Classificação</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Tipo</span>
                            </label>
                            <select
                                className="select select-bordered w-full focus:select-primary"
                                value={formData.type}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        type: e.target.value as MedicationType,
                                    })
                                }
                            >
                                {Object.entries(MEDICATION_TYPE_LABELS).map(
                                    ([key, label]) => (
                                        <option key={key} value={key}>
                                            {label}
                                        </option>
                                    )
                                )}
                            </select>
                        </div>
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Tipo de Controle
                                </span>
                            </label>
                            <select
                                className="select select-bordered w-full focus:select-primary"
                                value={formData.controlType}
                                onChange={e =>
                                    handleControlTypeChange(
                                        e.target
                                            .value as MedicationControlType
                                    )
                                }
                            >
                                {Object.entries(
                                    MEDICATION_CONTROL_TYPE_LABELS
                                ).map(([key, label]) => (
                                    <option key={key} value={key}>
                                        {label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Armazenamento
                                </span>
                            </label>
                            <select
                                className="select select-bordered w-full focus:select-primary"
                                value={formData.storageTemperature}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        storageTemperature: e.target
                                            .value as StorageTemperature,
                                    })
                                }
                            >
                                {Object.entries(STORAGE_TEMPERATURE_LABELS).map(
                                    ([key, label]) => (
                                        <option key={key} value={key}>
                                            {label}
                                        </option>
                                    )
                                )}
                            </select>
                        </div>
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Unidade
                                </span>
                            </label>
                            <select
                                className="select select-bordered w-full focus:select-primary"
                                value={formData.unit}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        unit: e.target.value as MedicationUnit,
                                    })
                                }
                            >
                                {Object.entries(MEDICATION_UNIT_LABELS).map(
                                    ([key, label]) => (
                                        <option key={key} value={key}>
                                            {label}
                                        </option>
                                    )
                                )}
                            </select>
                        </div>
                    </div>
                </div>

                <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                    <h2 className="text-lg font-semibold mb-4">
                        Apresentação e Composição
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Concentração
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full focus:input-primary"
                                placeholder="Ex: 500mg"
                                value={formData.concentration}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        concentration: e.target.value,
                                    })
                                }
                            />
                        </div>
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Apresentação
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full focus:input-primary"
                                placeholder="Ex: Caixa com 20 comprimidos"
                                value={formData.presentation}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        presentation: e.target.value,
                                    })
                                }
                            />
                        </div>
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Classe Terapêutica
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full focus:input-primary"
                                placeholder="Ex: Analgésico"
                                value={formData.therapeuticClass}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        therapeuticClass: e.target.value,
                                    })
                                }
                            />
                        </div>
                    </div>
                </div>

                <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                    <h2 className="text-lg font-semibold mb-4">
                        Controle de Estoque
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Estoque Mínimo
                                </span>
                            </label>
                            <input
                                type="number"
                                className="input input-bordered w-full focus:input-primary"
                                min="0"
                                value={formData.minimumStock}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        minimumStock: parseInt(e.target.value) || 0,
                                    })
                                }
                            />
                        </div>
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Estoque Máximo
                                </span>
                            </label>
                            <input
                                type="number"
                                className="input input-bordered w-full focus:input-primary"
                                min="0"
                                value={formData.maximumStock}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        maximumStock: parseInt(e.target.value) || 0,
                                    })
                                }
                            />
                        </div>
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Ponto de Reposição
                                </span>
                            </label>
                            <input
                                type="number"
                                className="input input-bordered w-full focus:input-primary"
                                min="0"
                                value={formData.reorderPoint}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        reorderPoint: parseInt(e.target.value) || 0,
                                    })
                                }
                            />
                        </div>
                    </div>
                </div>

                <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                    <h2 className="text-lg font-semibold mb-4">
                        Instruções e Alertas
                    </h2>
                    <div className="grid grid-cols-1 gap-4">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Instruções de Armazenamento
                                </span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered w-full focus:textarea-primary"
                                rows={3}
                                value={formData.storageInstructions}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        storageInstructions: e.target.value,
                                    })
                                }
                            />
                        </div>
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Precauções Especiais
                                </span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered w-full focus:textarea-primary"
                                rows={3}
                                value={formData.specialPrecautions}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        specialPrecautions: e.target.value,
                                    })
                                }
                            />
                        </div>
                        <div className="form-control">
                            <label className="label cursor-pointer justify-start gap-3">
                                <input
                                    type="checkbox"
                                    className="checkbox checkbox-warning"
                                    checked={formData.isHighAlert}
                                    onChange={e =>
                                        setFormData({
                                            ...formData,
                                            isHighAlert: e.target.checked,
                                        })
                                    }
                                />
                                <span className="label-text">
                                    Medicamento de Alto Alerta (requer dupla
                                    conferencia)
                                </span>
                            </label>
                        </div>
                    </div>
                </div>

                <div className="flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={() => navigate('/stock/medications')}
                        className="btn btn-ghost"
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary gap-2"
                        disabled={saving}
                    >
                        {saving ? (
                            <span className="loading loading-spinner loading-sm"></span>
                        ) : (
                            <MdSave className="text-xl" />
                        )}
                        {isEditing ? 'Atualizar' : 'Salvar'}
                    </button>
                </div>
            </form>
        </div>
    )
}
